/**
 * Discussion Post Component
 * Displays a single post with inline actions (Reply, Edit, Delete, Report)
 * Supports threaded replies with indentation
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Button,
  TextField,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Flag as FlagIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Reply as ReplyIcon,
  Block as BlockIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  InsertLink as InsertLinkIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material'
import { PostDto, FlagPostRequest, BlockUserRequest } from '@api/types/social'
import { formatDistanceToNow } from 'date-fns'
import { ReportContentDialog } from '@features/tickets/components'
import { CreateTicketRequest, TargetType } from '@api/types/tickets'
import { socialApi } from '@api/endpoints/social'
import { formatRelativeTime } from '@utils/dateUtils'
import '../styles/discussions.css'

// Helper to safely format dates
function safeFormatDistanceToNow(dateString: string | undefined | null): string {
  if (!dateString) return 'recently'
  try {
    // Parse UTC date string and convert to local time
    // If the string ends with 'Z' or has timezone offset (+ or - after the time portion), it's already timezone-aware
    // Otherwise append 'Z' to treat it as UTC
    const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)
    const utcString = hasTimezone ? dateString : dateString + 'Z'
    const date = new Date(utcString)
    if (isNaN(date.getTime())) return 'recently'
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return 'recently'
  }
}

// Helper to get initials from name
function getInitials(name: string | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

interface DiscussionPostProps {
  post: PostDto
  depth: number
  groupId: string
  discussionId: string
  isHighlighted?: boolean
  canReply?: boolean // Whether the user can reply to posts (default: true for backward compatibility)
  isModerator?: boolean
  onReply: (parentPostId: string, content: string, imageId?: string) => Promise<void>
  onEdit: (postId: string, content: string) => Promise<void>
  onDelete: (postId: string) => Promise<void>
  onFlag?: (postId: string, request: FlagPostRequest) => Promise<void> // Deprecated - now uses ticket system
  onHide?: (postId: string, reason: string) => Promise<void>
  onUnhide?: (postId: string) => Promise<void>
  isSubmitting?: boolean
}

const MAX_DEPTH = 3

export function DiscussionPost({
  post,
  depth,
  groupId,
  discussionId,
  isHighlighted = false,
  canReply = true,
  isModerator = false,
  onReply,
  onEdit,
  onDelete,
  onFlag,
  onHide,
  onUnhide,
  isSubmitting = false,
}: DiscussionPostProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const postRef = useRef<HTMLDivElement>(null)
  const [isReplying, setIsReplying] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const [editContent, setEditContent] = useState(post.content)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [blockDialogOpen, setBlockDialogOpen] = useState(false)
  const [hideDialogOpen, setHideDialogOpen] = useState(false)
  const [hideReason, setHideReason] = useState('')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Scroll to post if it matches the postId in URL
  useEffect(() => {
    const postId = searchParams.get('postId')
    if (postId === post.id && postRef.current) {
      setTimeout(() => {
        postRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Optional: highlight the post briefly
        postRef.current?.style.setProperty('background-color', 'rgba(25, 118, 210, 0.08)')
        setTimeout(() => {
          postRef.current?.style.removeProperty('background-color')
        }, 2000)
      }, 100)
    }
  }, [searchParams, post.id])

  const canNestReply = depth < MAX_DEPTH && canReply // Check both depth limit AND permission
  const indentLevel = Math.min(depth, MAX_DEPTH)

  const handleReply = async () => {
    if (!replyContent.trim()) return

    let imageId: string | undefined

    // Upload image first if one is selected
    if (selectedImage) {
      setUploadingImage(true)
      try {
        const response = await socialApi.uploadDiscussionImage(selectedImage)
        imageId = response.imageId
      } catch (err) {
        console.error('Error uploading image:', err)
        setUploadingImage(false)
        return
      }
      setUploadingImage(false)
    }

    await onReply(post.id, replyContent, imageId)
    setReplyContent('')
    setSelectedImage(null)
    setImagePreview(null)
    setIsReplying(false)
  }

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB')
      return
    }

    setSelectedImage(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Helper function to process image file from paste or drop
  const processImageFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB')
      return
    }

    setSelectedImage(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  // Handle paste event
  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile()
        if (file) {
          event.preventDefault()
          processImageFile(file)
          break
        }
      }
    }
  }

  // Handle drag over
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(true)
  }

  // Handle drag leave
  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
  }

  // Handle drop
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)

    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        processImageFile(file)
      }
    }
  }

  const handleEdit = async () => {
    if (!editContent.trim() || editContent === post.content) {
      setIsEditing(false)
      return
    }
    await onEdit(post.id, editContent)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      await onDelete(post.id)
    }
    setMenuAnchor(null)
  }

  const handleFlag = async (request: CreateTicketRequest) => {
    // Convert ticket request to flag post request
    const flagRequest: FlagPostRequest = {
      message: request.description,
      reportTags: [], // Can be extended later if we want to map ticket types to tags
      reportToGroupModerators: true, // Always report to group moderators
    }

    await socialApi.flagPost(post.id, flagRequest)
    setReportDialogOpen(false)
    setMenuAnchor(null)
  }

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true)
    setMenuAnchor(null)
  }

  const handleOpenBlockDialog = () => {
    setBlockDialogOpen(true)
    setMenuAnchor(null)
  }

  const handleBlockUser = async () => {
    try {
      const request: BlockUserRequest = {
        userId: post.authorId,
      }
      await socialApi.blockUser(request)
      setBlockDialogOpen(false)
      // Optionally refresh the page or show a success message
      window.location.reload()
    } catch (error) {
      console.error('Failed to block user:', error)
      alert('Failed to block user. Please try again.')
    }
  }

  const handleOpenHideDialog = () => {
    setHideDialogOpen(true)
    setMenuAnchor(null)
  }

  const handleHidePost = async () => {
    if (!onHide || !hideReason.trim()) return
    try {
      await onHide(post.id, hideReason)
      setHideDialogOpen(false)
      setHideReason('')
    } catch (error) {
      console.error('Failed to hide post:', error)
      alert('Failed to hide post. Please try again.')
    }
  }

  const handleUnhidePost = async () => {
    if (!onUnhide) return
    try {
      await onUnhide(post.id)
      setMenuAnchor(null)
    } catch (error) {
      console.error('Failed to unhide post:', error)
      alert('Failed to unhide post. Please try again.')
    }
  }

  const handleCopyPostLink = async () => {
    setMenuAnchor(null)
    const url = `${window.location.origin}/groups/${groupId}?tab=discussions&discussionId=${discussionId}&postId=${post.id}`

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = url
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
          document.execCommand('copy')
        } catch (err) {
          console.error('Fallback: Could not copy text', err)
        }
        document.body.removeChild(textArea)
      }
    } catch (err) {
      console.error('Failed to copy link:', err)
    }
  }

  const handleCancelEdit = () => {
    setEditContent(post.content)
    setIsEditing(false)
  }

  return (
    <Box
      ref={postRef}
      className={`discussion-post depth-${depth}`}
      id={`post-${post.id}`}
      sx={{
        position: 'relative',
      }}
    >
      <Paper elevation={0}>
        {/* Linked Post Indicator */}
        {isHighlighted && (
          <Box
            sx={{
              backgroundColor: '#e8f5e9',
              borderLeft: '4px solid #4caf50',
              borderRadius: 1,
              p: 1,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <InsertLinkIcon sx={{ color: '#4caf50', fontSize: 18 }} />
            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
              Linked post
            </Typography>
          </Box>
        )}

        {/* Post Header */}
        <Box className="discussion-post-meta">
          <Avatar
            src={post.authorAvatar}
            className="discussion-avatar"
            onClick={() => navigate(`/profile/${post.authorId}`)}
          >
            {getInitials(post.authorName)}
          </Avatar>
          <Typography
            component="span"
            className="discussion-author"
            onClick={() => navigate(`/profile/${post.authorId}`)}
          >
            {post.authorName || 'Unknown User'}
          </Typography>
          <IconButton
            size="small"
            className="discussion-menu-button"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            ⋯
          </IconButton>
        </Box>

        {/* Post Content */}
        {isEditing ? (
          <Box sx={{ mb: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={(e) => {
                // Submit on Enter (without Shift), allow Shift+Enter for new line
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if (editContent.trim() && !isSubmitting) {
                    handleEdit()
                  }
                }
              }}
              disabled={isSubmitting}
              size="small"
              className="discussion-edit-field"
            />
            <Box className="discussion-edit-actions">
              <Button
                size="small"
                variant="contained"
                onClick={handleEdit}
                disabled={isSubmitting || !editContent.trim()}
                className="discussion-button-small"
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
                className="discussion-button-small"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* Show masked content warning for regular users */}
            {post.isMasked && !isModerator ? (
              <Box
                sx={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <WarningIcon sx={{ color: '#ff9800' }} />
                <Typography sx={{ color: '#856404', fontStyle: 'italic' }}>
                  Content flagged by a moderator and muted
                </Typography>
              </Box>
            ) : (
              <>
                {/* Show hidden indicator for moderators */}
                {post.isHidden && isModerator && (
                  <Box
                    sx={{
                      backgroundColor: '#fff8e1',
                      borderLeft: '4px solid #ff9800',
                      borderRadius: 1,
                      p: 1,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <VisibilityOffIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 600 }}>
                        Hidden
                        {post.hiddenReason && `: ${post.hiddenReason}`}
                      </Typography>
                      {(post.hiddenByDisplayName || post.hiddenDate) && (
                        <Typography variant="caption" sx={{ color: '#e65100', display: 'block', mt: 0.5 }}>
                          {post.hiddenByDisplayName && `By ${post.hiddenByDisplayName}`}
                          {post.hiddenByDisplayName && post.hiddenDate && ' • '}
                          {post.hiddenDate && formatRelativeTime(post.hiddenDate)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {/* Display content */}
                <Typography className="discussion-content">
                  {post.content}
                </Typography>
                {/* Display attached image if present and not masked */}
                {post.imageUrl && (
                  <Box
                    component="img"
                    src={post.imageUrl}
                    alt="Post attachment"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 400,
                      borderRadius: 1,
                      mt: 1,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    onClick={() => window.open(post.imageUrl, '_blank')}
                  />
                )}
              </>
            )}
          </>
        )}

        {/* Inline Actions */}
        {!isEditing && (
          <Box className="discussion-actions">
            {canNestReply && (
              <button
                className="discussion-action-link"
                onClick={() => setIsReplying(!isReplying)}
              >
                Reply
              </button>
            )}
            {post.canEdit && (
              <button
                className="discussion-action-link"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            )}
            {post.canDelete && (
              <button
                className="discussion-action-link"
                onClick={handleDelete}
              >
                Delete
              </button>
            )}
            <Typography component="span" className="discussion-timestamp" sx={{ ml: 1 }}>
              {safeFormatDistanceToNow(post.createdDate)}
              {post.isEdited && ' • edited'}
            </Typography>
            {depth === MAX_DEPTH && (
              <Typography variant="caption" className="discussion-max-depth">
                (Max depth)
              </Typography>
            )}
          </Box>
        )}

        {/* Reply Input */}
        {isReplying && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: isDragOver ? '#e3f2fd' : '#f5f5f5',
              borderRadius: '8px',
              border: isDragOver ? '2px dashed #1976d2' : 'none',
              transition: 'all 0.2s',
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Image Preview */}
            {imagePreview && (
              <Box sx={{ mb: 1, position: 'relative', display: 'inline-block' }}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    maxWidth: 200,
                    maxHeight: 200,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              placeholder={isDragOver ? "Drop image here..." : "Write a reply..."}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                // Submit on Enter (without Shift), allow Shift+Enter for new line
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if (replyContent.trim() && !isSubmitting && !uploadingImage) {
                    handleReply()
                  }
                }
              }}
              disabled={isSubmitting || uploadingImage}
              size="small"
              className="discussion-reply-input"
            />

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />

            {/* Helper text - clickable */}
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: isSubmitting || uploadingImage || !!selectedImage ? 'default' : 'pointer',
                opacity: isSubmitting || uploadingImage || !!selectedImage ? 0.5 : 1,
                '&:hover': {
                  opacity: isSubmitting || uploadingImage || !!selectedImage ? 0.5 : 0.8,
                },
              }}
            >
              <CameraIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Paste or drag images, or click the camera icon to upload
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setIsReplying(false)
                    setReplyContent('')
                    setSelectedImage(null)
                    setImagePreview(null)
                  }}
                  disabled={isSubmitting || uploadingImage}
                  className="discussion-button-small"
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleReply}
                  disabled={isSubmitting || uploadingImage || !replyContent.trim()}
                  className="discussion-reply-button"
                >
                  {uploadingImage ? <CircularProgress size={16} /> : 'Reply'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleCopyPostLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>
        {isModerator && !post.isHidden && (
          <MenuItem onClick={handleOpenHideDialog}>
            <ListItemIcon>
              <VisibilityOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Hide Post</ListItemText>
          </MenuItem>
        )}
        {isModerator && post.isHidden && (
          <MenuItem onClick={handleUnhidePost}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unhide Post</ListItemText>
          </MenuItem>
        )}
        {post.canEdit && (
          <MenuItem
            onClick={() => {
              setIsEditing(true)
              setMenuAnchor(null)
            }}
          >
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {post.canDelete && (
          <MenuItem onClick={handleDelete}>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
        {!post.canEdit && !post.canDelete && (
          <>
            <MenuItem onClick={handleOpenReportDialog}>
              <ListItemIcon>
                <FlagIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Report</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleOpenBlockDialog}>
              <ListItemIcon>
                <BlockIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Block User</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Report Dialog */}
      <ReportContentDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        onSubmit={handleFlag}
        targetType={TargetType.Post}
        targetId={post.id}
        contentPreview={post.content}
        isSubmitting={isSubmitting}
      />

      {/* Block User Confirmation Dialog */}
      <Dialog
        open={blockDialogOpen}
        onClose={() => setBlockDialogOpen(false)}
      >
        <DialogTitle>Block User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to block {post.authorName}? You will no longer see their posts or be able to interact with them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleBlockUser} color="error" variant="contained">
            Block User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hide Post Dialog */}
      <Dialog
        open={hideDialogOpen}
        onClose={() => setHideDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Hide Post</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will hide the post content from regular users. You and other moderators will still be able to see it.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason for hiding (required)"
            value={hideReason}
            onChange={(e) => setHideReason(e.target.value)}
            placeholder="Enter the reason for hiding this post..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setHideDialogOpen(false)
            setHideReason('')
          }} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleHidePost}
            color="warning"
            variant="contained"
            disabled={!hideReason.trim()}
          >
            Hide Post
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
